export const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

export const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

export const BODY_FONT = { name: "Calibri", size: 11 };
export const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;
export const COL_M = 13;
export const COL_N = 14;
export const COL_O = 15;
export const COL_P = 16;
export const COL_Q = 17;
export const COL_R = 18;
export const COL_S = 19;
export const COL_T = 20;
export const COL_U = 21;
export const COL_V = 22;
export const COL_END = COL_V;

export const COL_VALUE_START = COL_U;
export const COL_VALUE_END = COL_V;
export const COL_LABEL_END = COL_T;

export const BENEFIT_KEYS = [
  ["healthInsurance", "Health insurance"],
  ["accidentInsurance", "Accident insurance"],
  ["maternityBenefits", "Maternity benefits"],
  ["paternityBenefits", "Paternity Benefits"],
  ["dayCareFacilities", "Day Care facilities"],
];

export function borderCell(cell) {
  cell.border = THIN_BLACK;
}

export function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "View Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

export function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col++) {
    borderCell(ws.getCell(row, col));
  }
}

export function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

export function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value);
  return str.includes("%") ? str : `${str}%`;
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toFixed(2);
  return String(value);
}

export function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, { bold: true, align: "center", fill });
  return row + 1;
}

export function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, { align: "left", vertical: "top", bold: options.bold });
    return row + 1;
  }
  const valueCol = options.valueCol || COL_E;
  const valueEnd = options.valueEnd || COL_END;
  mergeRow(ws, row, COL_D, valueCol - 1, label, { align: "left", vertical: "top", bold: options.bold });
  mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
    align: options.valueAlign ?? (options.link ? "center" : "left"),
    vertical: "top",
    link: options.link,
  });
  return row + 1;
}

export function addDetailLinkRow(ws, row, label, sheetName) {
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, "View Details", {
    align: "center",
    link: `#'${sheetName}'!D3`,
  });
  return row + 1;
}

export function addTextOrDetailsRow(ws, row, label, text) {
  const value = safeText(text, "View Details");
  const hasText = text !== null && text !== undefined && String(text).trim() !== "";
  return addQuestionRow(ws, row, label, value, {
    valueCol: COL_VALUE_START,
    valueEnd: COL_VALUE_END,
    valueAlign: hasText ? "left" : "center",
  });
}

function benefitValue(section, benefitKey, genderPrefix) {
  const benefit = section?.[benefitKey] || {};
  if (genderPrefix === "male") {
    return { num: formatNumber(benefit.male), pct: formatPercent(benefit.malePercentage) };
  }
  if (genderPrefix === "female") {
    return { num: formatNumber(benefit.female), pct: formatPercent(benefit.femalePercentage) };
  }
  if (genderPrefix === "total") {
    const male = Number(benefit.male) || 0;
    const female = Number(benefit.female) || 0;
    const total = male + female;
    if (!total) return { num: "-", pct: "-" };
    return { num: formatNumber(total), pct: formatPercent((total / (Number(benefit.total) || total)) * 100) };
  }
  return { num: "-", pct: "-" };
}

export function addWellBeingBlock(ws, row, measures, config) {
  const { sectionKey, maleCountKey, femaleCountKey, blockLabel } = config;
  const section = measures?.[sectionKey] || {};

  row = addQuestionRow(ws, row, blockLabel, "", { fullWidth: true, bold: true });

  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  let col = COL_F;
  BENEFIT_KEYS.forEach(([, label]) => {
    mergeRow(ws, row, col, col + 1, label, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
    col += 2;
  });
  row += 1;

  col = COL_F;
  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "", { fill: TABLE_HEADER_FILL });
  BENEFIT_KEYS.forEach(() => {
    setCell(ws, row, col, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
    setCell(ws, row, col + 1, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
    col += 2;
  });
  row += 1;

  const genderRows = [
    { label: "Male", prefix: "male", total: measures?.maleEmployees?.[maleCountKey] },
    { label: "Female", prefix: "female", total: measures?.femaleEmployees?.[femaleCountKey] },
    { label: "Other", prefix: "other", total: null },
    {
      label: "Total",
      prefix: "total",
      total:
        (Number(measures?.maleEmployees?.[maleCountKey]) || 0) +
        (Number(measures?.femaleEmployees?.[femaleCountKey]) || 0),
    },
  ];

  genderRows.forEach(({ label, prefix, total }) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, total ? formatNumber(total) : "-", { align: "center" });
    col = COL_F;
    BENEFIT_KEYS.forEach(([key]) => {
      const { num, pct } = benefitValue(section, key, prefix);
      setCell(ws, row, col, num, { align: "center" });
      setCell(ws, row, col + 1, pct, { align: "center" });
      col += 2;
    });
    row += 1;
  });

  return row;
}

export function addMembershipTable(ws, row, block, fyCurrent, fyPrevious) {
  const categories = [
    ["Male", "totalMaleEmployees", "NoOfMaleEmployeesCategory", "totalMaleEmployeesPercentage"],
    ["Female", "totalFemaleEmployees", "NoOfFemaleEmployeesCategory", "totalfemaleEmployeesPercentage"],
    ["Total", "totalEmployeesCategory", "NoofEmployeesCategory", null],
  ];

  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Total (C)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "No. (D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "% (D / C)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  categories.forEach(([label, totalKey, unionKey, pctKey]) => {
    const current = block?.currentFinancialYear || {};
    const previous = block?.previousFinancialYear || {};
    const currentTotal = current?.[totalKey];
    const currentUnion = current?.[unionKey];
    const previousTotal = previous?.[totalKey];
    const previousUnion = previous?.[unionKey];
    let currentPct = pctKey ? current?.[pctKey] : null;
    if (!currentPct && currentTotal && currentUnion) {
      currentPct = ((Number(currentUnion) / Number(currentTotal)) * 100).toFixed(2);
    }
    let previousPct = pctKey ? previous?.[pctKey] : null;
    if (!previousPct && previousTotal && previousUnion) {
      previousPct = ((Number(previousUnion) / Number(previousTotal)) * 100).toFixed(2);
    }

    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(currentTotal), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(currentUnion), { align: "center" });
    setCell(ws, row, COL_G, formatPercent(currentPct), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(previousTotal), { align: "center" });
    setCell(ws, row, COL_I, formatNumber(previousUnion), { align: "center" });
    setCell(ws, row, COL_J, formatPercent(previousPct), { align: "center" });
    row += 1;
  });

  return row;
}

export function addTrainingTable(ws, row, currentBlock, previousBlock, fyCurrent, fyPrevious) {
  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_I, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_J, COL_N, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_F, COL_G, "On Health and safety measures", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_I, "On Skill upgradation", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Total (D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_K, COL_L, "On Health and safety measures", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_M, COL_N, "On Skill upgradation", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_F, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "No. (C)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "% (C / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_K, "No. (E)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_L, "% (E / D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_M, "No. (F)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_N, "% (F / D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const categories = [
    ["Male", "totalMale", "HealthMaleNumber", "HealthMalePercentage", "SkillMaleNumber", "SkillMalePercentage"],
    ["Female", "totalFemale", "HealthFemaleNumber", "HealthFemalePercentage", "SkillFemaleNumber", "SkillFemalePercentage"],
    ["Other", null, null, null, null, null],
    ["Total", "Total", "HealthTotal", null, "SkillTotal", null],
  ];

  categories.forEach(([label, totalKey, hsNum, hsPct, skNum, skPct]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(currentBlock?.[totalKey]), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(currentBlock?.[hsNum]), { align: "center" });
    setCell(ws, row, COL_G, formatPercent(currentBlock?.[hsPct]), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(currentBlock?.[skNum]), { align: "center" });
    setCell(ws, row, COL_I, formatPercent(currentBlock?.[skPct]), { align: "center" });
    setCell(ws, row, COL_J, formatNumber(previousBlock?.[totalKey]), { align: "center" });
    setCell(ws, row, COL_K, formatNumber(previousBlock?.[hsNum]), { align: "center" });
    setCell(ws, row, COL_L, formatPercent(previousBlock?.[hsPct]), { align: "center" });
    setCell(ws, row, COL_M, formatNumber(previousBlock?.[skNum]), { align: "center" });
    setCell(ws, row, COL_N, formatPercent(previousBlock?.[skPct]), { align: "center" });
    row += 1;
  });

  return row;
}

export function addPerformanceTable(ws, row, block, fyLabel) {
  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_G, fyLabel, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const categories = [
    ["Male", "male"],
    ["Female", "female"],
    ["Other", "other"],
    ["Total", "total"],
  ];

  categories.forEach(([label, key]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(block?.totalEmployee?.[key]), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(block?.employee?.[key]), { align: "center" });
    setCell(ws, row, COL_G, formatPercent(block?.Percentage?.[key]), { align: "center" });
    row += 1;
  });

  return row;
}

export function setPrinciple3ColumnWidths(ws) {
  const widths = {
    A: 2, B: 2, C: 2, D: 18, E: 10, F: 10, G: 10, H: 10, I: 10, J: 10,
    K: 10, L: 10, M: 10, N: 10, O: 10, P: 10, Q: 10, R: 10, S: 10, T: 10, U: 10, V: 10,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
}
